import { ViscaBridgeRequest } from '@tryiris-ai/common.protojs/joystick/v1/joystick';
import type { Ambit } from '@studio/models/ptz';
import type { Device } from '@studio/models/device';
import { locateProxy } from '#~/agent/index.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { CommandDeviceMove, CommandDeviceStop } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { normSpeed } from './helpers.js';
import { isTrialCameraLocked } from '#~/api/billing/camera-access.js';

interface MovementState {
  pan: number;
  tilt: number;
  zoom: number;
}

export class Repeater {
  private moveState: MovementState;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private deviceID: Device['id'];

  constructor(deviceId: Device['id']) {
    this.deviceID = deviceId;
    this.moveState = {
      pan: 0,
      tilt: 0,
      zoom: 0
    };
  }

  /**
   * Add updates the movement state and manages the interval
   */
  public add(req: ViscaBridgeRequest): void {
    const newPan = normSpeed.pan(req.move?.pan?.speed, req.move?.pan?.direction);
    const newTilt = normSpeed.tilt(req.move?.tilt?.speed, req.move?.tilt?.direction);
    const newZoom = normSpeed.zoom(req.move?.zoom?.speed, req.move?.zoom?.direction);

    const panTiltShouldStop = req.moveStop?.panTilt ?? false;
    const zoomShouldStop = req.moveStop?.zoom ?? false;

    if (panTiltShouldStop) {
      this.moveState.pan = 0;
      this.moveState.tilt = 0;
    }
    if (zoomShouldStop) this.moveState.zoom = 0;

    // Always clear the interval.
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (panTiltShouldStop || zoomShouldStop) {
      this.stop({
        pan: panTiltShouldStop,
        tilt: panTiltShouldStop,
        zoom: zoomShouldStop
      });
      // If it's a stop command, there's nothing else to do.
      return;
    }

    // Execute the command immediately.
    this.move(newPan, newTilt, newZoom);

    this.intervalId = setInterval(() => {
      this.move(newPan, newTilt, newZoom);
    }, 250);
  }

  move(pan?: number, tilt?: number, zoom?: number): void {
    const proxy = locateProxy(this.deviceID);

    if (!proxy) {
      console.error(`>>> IPController move: No proxy found for device ${this.deviceID}`);
      return;
    }
    // Re-evaluate each repeat rather than retaining permission from add().
    if (isTrialCameraLocked(proxy.roomId, proxy.id, this.deviceID)) return;

    const mutation: DeepPartial<CommandDeviceMove> = {
      id: this.deviceID,
      panTilt:
        pan || tilt
          ? {
              pan,
              tilt
            }
          : undefined,
      zoom: zoom ? { zoom } : undefined
    };

    const mutB = CommandDeviceMove.encode(CommandDeviceMove.fromJSON(mutation)).finish();

    proxy.connection.publish(`direct.authenticated.${proxy.id}.move`, mutB);
  }

  stop(intent: Ambit): boolean {
    const proxy = locateProxy(this.deviceID);

    if (!proxy) {
      console.error(`>>> IPController stop: No proxy found for device ${this.deviceID}`);
      return false;
    }
    const mutation: DeepPartial<CommandDeviceStop> = {
      focus: false,
      id: this.deviceID,
      panTilt: intent.pan || intent.tilt,
      zoom: intent.zoom
    };

    const mutB = CommandDeviceStop.encode(CommandDeviceStop.fromPartial(mutation)).finish();

    proxy.connection.publish(`direct.authenticated.${proxy.id}.stop`, mutB);
    // Local enqueue only — not camera acknowledgement.
    return true;
  }
  /**
   * Close stops all active command repetitions.
   * publishedStop is false when a moving repeater had no proxy — local
   * interval is still cleared, but physical halt is unconfirmed.
   */
  public close(): { publishedStop: boolean } {
    // A live interval is local repetition still armed — not proof the camera
    // physically received the last command. Gating on it keeps an idle camera
    // off the wire (setDeviceID closes the repeater on every camera switch).
    const wasMoving = this.intervalId !== null;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    let publishedStop = true;
    // Explicit stop, not the encoder's move dead-man (500 ms by default,
    // model-configurable) — the camera must halt the moment the loop ends.
    if (wasMoving) {
      publishedStop = this.stop({ pan: true, tilt: true, zoom: true });
    }
    this.moveState = {
      pan: 0,
      tilt: 0,
      zoom: 0
    };
    return { publishedStop };
  }
}
